/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { UserPlus, Gift, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { generateColleagueRef, listMyReferrals, redeemColleagueRef } from '@/lib/growth';

export const ColleagueReferralPanel = () => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [redeem, setRedeem] = useState('');
  const [copied, setCopied] = useState('');

  const load = async () => {
    try { setItems((await listMyReferrals()).items || []); } catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const gen = async () => {
    setBusy(true);
    try { await generateColleagueRef(); toast.success('Codice referral generato'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
    finally { setBusy(false); }
  };

  const doRedeem = async () => {
    if (!redeem.trim()) return;
    try {
      const r = await redeemColleagueRef(redeem.trim().toUpperCase());
      toast.success(`+${r.reward_months} mese Premium per te e per il collega`);
      setRedeem('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const copy = async (code) => {
    try { await navigator.clipboard.writeText(code); setCopied(code); setTimeout(() => setCopied(''), 1500); } catch {}
  };

  const active = items.filter((i) => i.status === 'active');
  const redeemed = items.filter((i) => i.status === 'redeemed');

  return (
    <Card data-testid="colleague-referral-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-display flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-primary" /> Invita un collega
        </CardTitle>
        <CardDescription>
          Quando un collega verificato riscatta il tuo codice, entrambi ottenete <b>1 mese Premium gratis</b>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={gen} disabled={busy} data-testid="gen-colleague-ref">
            <Gift className="h-3.5 w-3.5 mr-1" /> Genera codice
          </Button>
        </div>
        {active.length === 0 && <p className="text-xs text-muted-foreground">Nessun codice attivo. Generane uno per iniziare.</p>}
        {active.map((c) => (
          <div key={c.id} className="flex items-center gap-2 rounded-md border border-border p-2">
            <span className="font-mono font-semibold text-sm flex-1">{c.code}</span>
            <Badge variant="outline" className="text-[10px]">attivo</Badge>
            <Button size="icon" variant="ghost" onClick={() => copy(c.code)} className="h-7 w-7" data-testid={`copy-${c.code}`}>
              {copied === c.code ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
          </div>
        ))}
        {redeemed.length > 0 && (
          <div className="text-[11px] text-muted-foreground">Riscattati: {redeemed.length} · mesi guadagnati: {redeemed.length}</div>
        )}

        <div className="pt-3 border-t border-border">
          <label className="text-xs text-muted-foreground">Hai un codice da un collega?</label>
          <div className="flex gap-2 mt-1">
            <Input value={redeem} onChange={(e) => setRedeem(e.target.value.toUpperCase().slice(0, 32))}
                   placeholder="COLXXXXXX" data-testid="colleague-redeem-input" />
            <Button size="sm" onClick={doRedeem} disabled={!redeem.trim()} data-testid="colleague-redeem-btn">Riscatta</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ColleagueReferralPanel;
